import { Button } from "@/components/ui/button";
import { A } from "@solidjs/router";
import { ExternalLink, Lock, Play } from "lucide-solid";
import { For, Show, createMemo } from "solid-js";
import { useService } from "solid-services";
import { css } from "styled-system/css";
import { Box, Flex } from "styled-system/jsx";
import { getCourseDecor } from "../courseDecor";
import { useCourseOverview } from "../useCourseOverview";

interface ResourceLink {
  label: string
  href: string
}

function splitResources(html?: string | null): { intro: string; links: ResourceLink[] } {
  if (!html) return { intro: '', links: [] }

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const anchors = Array.from(doc.querySelectorAll('a'))
  const links = anchors
    .map((a) => ({ label: a.textContent?.trim() || '', href: a.getAttribute('href') || '' }))
    .filter((link): link is ResourceLink => !!link.label && !!link.href)

  anchors.forEach((a) => a.replaceWith(doc.createTextNode(a.textContent || '')))

  return { intro: doc.body.innerHTML, links }
}

export default function () {
  const {
    activeLessonOverviewQuery,
    currentLessonSlug,
    courseStatus,
    isLocked,
    totalSections,
    totalLessonsCount,
    totalQuizzesCount,
    totalContentHours,
    totalItemsCount,
    completedCount,
    percentComplete,
  } = useService(useCourseOverview)()

  const decor = createMemo(() => getCourseDecor(activeLessonOverviewQuery.data?.title || ''))
  const parsed = createMemo(() => splitResources(activeLessonOverviewQuery.data?.description))

  const ctaLabel = () => {
    const status = courseStatus()
    if (status === 'not_started') return 'Start'
    if (status === 'completed') return 'Review'
    return 'Continue'
  }

  return (
    <Box
      bg="editorialPanel"
      border="1px solid var(--colors-editorial-hair)"
      borderRadius="14px"
      p={{ base: '20px 22px', smDown: '17px 16px' }}
      mb="22px"
      style={{ '--c': decor().color }}
      class={css({ boxShadow: 'inset 3px 0 0 var(--c), 0 1px 2px rgba(20,22,27,.04)' })}
    >
      <Flex justify="space-between" align="flex-start" gap="18px" flexWrap="wrap" mb="9px">
        <Box
          fontSize={{ base: '22px', smDown: '20px' }}
          fontWeight="800"
          letterSpacing="-.02em"
          color="editorialInk"
        >
          {activeLessonOverviewQuery.data?.title}
        </Box>

        <Show
          when={!isLocked()}
          fallback={
            <Flex
              align="center"
              gap="7px"
              h="38px"
              px="16px"
              border="1px solid var(--colors-editorial-hair-strong)"
              borderRadius="9px"
              color="editorialInk3"
              fontSize="13px"
              fontWeight="700"
              flex="none"
            >
              <Lock size={15} />
              Locked
            </Flex>
          }
        >
          <A href={`/courses/${activeLessonOverviewQuery.data?.slug}/${currentLessonSlug()}`}>
            <Button
              type="button"
              bg="editorialAccent"
              color="editorialBadgeFg"
              h="38px"
              px="16px"
              borderRadius="9px"
              fontWeight="700"
              fontSize="13px"
              flex="none"
              _hover={{ filter: 'brightness(1.04)' }}
            >
              <Play size={15} fill="currentColor" />
              {ctaLabel()}
            </Button>
          </A>
        </Show>
      </Flex>

      <Show when={parsed().intro}>
        <Box
          fontSize="13px"
          color="editorialInk2"
          lineHeight="1.5"
          maxWidth="74ch"
          mb="15px"
          innerHTML={parsed().intro}
        />
      </Show>

      <Flex fontSize="12.5px" color="editorialInk2" gap="6px" flexWrap="wrap" align="baseline" mb="15px">
        <span><b style={{ color: 'var(--colors-editorial-ink)' }}>{totalSections()}</b> sections</span>
        <span>·</span>
        <span><b style={{ color: 'var(--colors-editorial-ink)' }}>{totalLessonsCount()}</b> topics</span>
        <span>·</span>
        <span><b style={{ color: 'var(--colors-editorial-ink)' }}>{totalQuizzesCount()}</b> quizzes</span>
        <span>·</span>
        <span>{totalContentHours()} of content</span>
      </Flex>

      <Flex align="center" gap="12px" flexWrap="wrap" mb={parsed().links.length ? '17px' : '0'}>
        <Box
          flex="1"
          minWidth="120px"
          maxWidth="440px"
          height="7px"
          borderRadius="999px"
          background="color-mix(in srgb, var(--colors-editorial-ink) 11%, transparent)"
          overflow="hidden"
        >
          <Box height="100%" borderRadius="999px" bg="editorialAccent" style={{ width: `${percentComplete() ?? 0}%` }} />
        </Box>
        <Box fontSize="12px" color="editorialInk2" whiteSpace="nowrap">
          {percentComplete() ?? 0}% complete · {completedCount()} of {totalItemsCount()} lessons
        </Box>
      </Flex>

      <Show when={parsed().links.length}>
        <Flex align="center" gap="8px" flexWrap="wrap">
          <Box
            fontSize="10.5px"
            textTransform="uppercase"
            letterSpacing=".07em"
            color="editorialInk3"
            fontWeight="700"
            mr="3px"
          >
            Resources
          </Box>
          <For each={parsed().links}>
            {(link) => (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                class={css({
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '7px',
                  height: '32px',
                  padding: '0 12px',
                  border: '1px solid var(--colors-editorial-hair-strong)',
                  borderRadius: '8px',
                  bg: 'editorialPanel',
                  color: 'editorialInk2',
                  fontSize: '12.5px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { bg: 'editorialHover', color: 'editorialInk' },
                })}
              >
                <ExternalLink size={14} />
                {link.label}
              </a>
            )}
          </For>
        </Flex>
      </Show>
    </Box>
  )
}
